/**
 * print-path.rules.ts
 *
 * Which print path a surface uses when the shared receiving flow runs on it.
 *
 * Two paths exist, and they are not interchangeable:
 *   - backend  — POST /V1/print, which drives the Zebra label printers and the
 *                copier, and is what renders the per-item QR blocks.
 *   - browser  — the label is rendered client-side and sent through the browser's
 *                print dialog, which is how the desktop locations reach their
 *                existing Dymo printers.
 *
 * The facility decides, not the device:
 *
 *   - A location with a Zebra printer configured (`zebraPrinterName` in
 *     locations-config.json — today only Kent) uses the backend path, on desktop
 *     as well as mobile. The printer is in that building; which machine the
 *     operator happens to be standing at does not change where the label comes
 *     out, and desktop is where the per-item QR blocks are most useful.
 *
 *   - Every other location uses the browser path, to the Dymo attached to the
 *     desktop. A backend job there would queue at hardware that does not exist.
 *
 * Keying off the configured printer rather than the name "Kent" means a facility
 * joins the backend path the moment its printer is filled in — no code change,
 * and no list of location names to keep in sync.
 *
 * `printMode.mobile` can still force the browser path where a backend printer
 * exists; it is the standing config escape hatch.
 *
 * On top of that standing configuration sits the per-device outage switch —
 * {@link PrintFallbackMode}, set by the person standing at the printer. The
 * facility rules above answer for a working plant; the fallback mode answers
 * "and what if this hardware is dead right now". Because a dead Zebra does not
 * take the copier down with it, that answer is PER DOCUMENT, which is what
 * {@link resolvePrintRoute} returns.
 */

import { PrintMode } from '../../../models/assets-data/receiving/ReceivingLogFormConfig.interface';
import { Location } from '../../../models/assets-data/LocationConfig.interface';
import { PrintKind } from '../../../models/printing/PrintRequest';

/**
 * The device-local print outage switch (see PrintFallbackService).
 *
 * Not a total order — `zebraDown` and `copierDown` are independent failures of
 * two different printers, and neither implies the other. The one ordering claim
 * that does survive: `printApiDown` subsumes both, since it takes the whole
 * backend path away regardless of which piece of hardware is actually fine.
 *
 *   - `normal`       — today's behaviour, unchanged.
 *   - `zebraDown`    — the label printer is dead; the Print API and copier are
 *                      fine, so copier sheets still go to the backend.
 *   - `copierDown`   — the copier is dead; the Print API and Zebra are fine, so
 *                      labels still go to the backend.
 *   - `printApiDown` — nothing reaches the backend print path at all.
 */
export type PrintFallbackMode = 'normal' | 'zebraDown' | 'copierDown' | 'printApiDown';

/**
 * Whether MODE keeps labels printing automatically, to the Zebra.
 *
 * Moved here (from the Printing screen, its original home) so the automatic
 * fallback offer in `PrintService` can combine a newly-failed document with
 * whatever mode is already stored using the exact same per-document facts the
 * Printing screen shows — one definition of "automatic", not two.
 */
export function labelAutomatic(mode: PrintFallbackMode): boolean {
  return mode !== 'zebraDown' && mode !== 'printApiDown';
}

/** Whether MODE keeps copier sheets printing automatically, to the copier. */
export function sheetAutomatic(mode: PrintFallbackMode): boolean {
  return mode !== 'copierDown' && mode !== 'printApiDown';
}

/**
 * The inverse of {@link labelAutomatic} / {@link sheetAutomatic}: the one mode
 * that claims exactly this pair of per-document facts. Total over all four
 * combinations, so nothing here needs an ordering assumption or a default
 * branch — every pair of booleans names a real mode.
 */
export function modeFor(labelAuto: boolean, sheetAuto: boolean): PrintFallbackMode {
  if (labelAuto && sheetAuto) { return 'normal'; }
  if (!labelAuto && sheetAuto) { return 'zebraDown'; }
  if (labelAuto && !sheetAuto) { return 'copierDown'; }
  return 'printApiDown';
}

/**
 * The mode that results from a device already in CURRENT reporting ADDITIONAL
 * as well — used when a fresh backend print failure arrives on top of
 * whatever this device already has stored.
 *
 * Combines per document rather than picking the "more severe" of the two
 * whole-job modes: a device already in `zebraDown` whose copier then fails
 * lands on `printApiDown` (both documents down), but a device in `zebraDown`
 * told the COPIER is fine again would have no way to express that from an
 * ordered scale. Per-document AND is the total, order-free version of
 * "worse wins".
 */
export function combineFallbackModes(
  current: PrintFallbackMode,
  additional: PrintFallbackMode,
): PrintFallbackMode {
  return modeFor(
    labelAutomatic(current) && labelAutomatic(additional),
    sheetAutomatic(current) && sheetAutomatic(additional),
  );
}

/**
 * Whether a failed backend print call failed at the TRANSPORT level — no
 * response reached the browser at all — rather than the print API responding
 * and naming a problem with the request itself.
 *
 * `status === 0` is Angular's signal for both a network error and a timeout
 * (there is no HTTP response to read a code from). Anything `>= 500` is
 * grouped in with it: this frontend cannot tell a gateway's own 5xx apart
 * from the print API reporting a hardware fault as one, and the safer guess
 * under that ambiguity is the one that offers the operator the fallback
 * rather than the one that stays quiet. The offer is never applied silently
 * (see `PrintService`), so an over-broad guess here costs a dismissible
 * prompt, not a wrongly-flipped switch.
 */
export function isTransportLevelFailure(status: number): boolean {
  return status === 0 || status >= 500;
}

/**
 * Maps a failed backend print attempt to the fallback mode it should offer to
 * turn on.
 *
 * `docs` are every document the failed request(s) asked the backend to print
 * — `printDocsFor(kind)` for a single request, or the union across a batch.
 * `transportLevel` (see {@link isTransportLevelFailure}) overrides the
 * document mapping: a request that never reached the print API says nothing
 * reliable about which ONE printer is at fault, so the conservative reading
 * is that neither can be trusted right now.
 *
 * Returns `normal` only when `docs` is empty, which should not happen in
 * practice — a print request always asks for at least one document — but a
 * caller with nothing to map should not be told to turn anything on.
 */
export function modeForFailedDocs(docs: PrintDoc[], transportLevel: boolean): PrintFallbackMode {
  if (transportLevel) { return 'printApiDown'; }
  const label = docs.includes('label');
  const sheet = docs.includes('sheet');
  if (label && sheet) { return 'printApiDown'; }
  if (label) { return 'zebraDown'; }
  if (sheet) { return 'copierDown'; }
  return 'normal';
}

/**
 * One printable document: the label, or the copier sheet.
 *
 * THE NAMES ARE FIXED. User-facing copy anywhere in the app uses exactly these
 * words, and the reason is that operators act on them:
 *
 *   - **label** — the sticker that goes on the box. It comes off a **Zebra**
 *     (the shop printer the print API drives) or a **Dymo** (the one attached to
 *     a desktop computer). Name the machine when it matters which; the document
 *     is always "label".
 *   - **copier sheet** — the 8x11 page. Never "cover sheet", never "full sheet".
 *     The desktop form's own print-type dropdown has said "Copier Sheet" since
 *     before this rule existed; everything else follows it.
 *
 * THE LABEL CHANGES MACHINE when a job falls to the browser path; the copier
 * sheet changes something else. The label moves from the Zebra to a Dymo beside
 * a desktop computer — two printers, two places. The copier sheet does not
 * simply move to "a desktop computer" either: through the backend print API
 * there is exactly ONE copier, the one named in `copierPrinterName`, and the
 * sheet always goes there automatically. Through the browser print dialog the
 * operator can send it to ANY copier on the network — not necessarily the same
 * one. That is a wider choice, not a degraded version of the same destination,
 * and copy that treats the browser path as merely "the same copier, by hand"
 * sends an operator looking for the page at a machine it may never reach. The
 * axis to write on is AUTOMATIC vs BY HAND, with the destination (and, for the
 * copier, the fact that it's a free choice on the browser path) named
 * separately.
 *
 * NEVER use "labels" to mean both documents. A message reading "its labels will
 * print without QR codes" on a job that is printing only a copier sheet
 * describes a document the operator did not ask for, and they will go looking
 * for the sticker it promised. Say "labels and copier sheets", or name neither.
 *
 * And say "a desktop computer", not "this computer". Every one of these screens
 * opens on a phone as readily as at a desk, and a phone cannot print: the
 * browser path only reaches a printer from a desktop. "This computer" read on a
 * phone promises paper that is not coming.
 */
export type PrintDoc = 'label' | 'sheet';

/**
 * Where each document of one print job goes. The three lists are disjoint and
 * together account for every document the job asked for.
 */
export interface PrintRoute {
  /** What still goes to the backend print API, or null when nothing does. */
  backendKind: PrintKind | null;
  /** What renders client-side through the browser's print dialog. */
  browserDocs: PrintDoc[];
  /**
   * What cannot print from this surface at all — the caller must TELL the
   * operator what to print and where, rather than silently dropping it.
   */
  deferredDocs: PrintDoc[];
}

/**
 * What the print API can reach at one facility, PER DOCUMENT.
 *
 * Two printers, two independent answers. The label goes to a Zebra and the cover
 * sheet goes to a copier; a facility can have either, both, or neither, and until
 * now the code asked one question ("is there a Zebra?") and routed both documents
 * on it. That sent a configured copier's sheets to a browser print dialog because
 * the label printer beside it had not been installed yet.
 */
export interface PrintCapability {
  /** A Zebra label printer the print API can target (`zebraPrinterName`). */
  hasZebraPrinter: boolean;
  /** A copier the print API can target (`copierPrinterName`). */
  hasCopierPrinter: boolean;
}

/** Everything {@link resolvePrintRoute} needs to know about this surface. */
export interface PrintRouteContext {
  /** Running on a mobile device — where the browser print dialog reaches no printer. */
  isMobile: boolean;
  /** `printMode.mobile` from the receiving-log form config. */
  configuredMode: PrintMode;
  /** Which printers this facility has, per document. */
  capability: PrintCapability;
  /** The device-local outage switch. */
  fallbackMode: PrintFallbackMode;
}

/** The documents a print kind covers. */
export function printDocsFor(kind: PrintKind): PrintDoc[] {
  if (kind === 'Zebra') { return ['label']; }
  if (kind === 'Copier') { return ['sheet']; }
  return ['label', 'sheet'];
}

/** The print kind covering exactly this set of documents (null for none). */
function kindForDocs(docs: PrintDoc[]): PrintKind | null {
  const label = docs.includes('label');
  const sheet = docs.includes('sheet');
  if (label && sheet) { return 'Both'; }
  if (label) { return 'Zebra'; }
  if (sheet) { return 'Copier'; }
  return null;
}

/**
 * Routes one print job, document by document.
 *
 * Three questions in order, per document:
 *
 *   1. Can the backend print it? Only where the facility has a printer and the
 *      config hasn't forced the browser — and, now, only where the relevant
 *      hardware is not reported down. `zebraDown` takes the label off the
 *      backend and leaves the sheet on it; `printApiDown` takes both off.
 *   2. Failing that, can this surface browser-print it? Desktop can — that is
 *      the Dymo attached to the machine. A phone cannot: the browser dialog on
 *      a device in someone's hand reaches no printer in the warehouse.
 *   3. Failing both, the document is DEFERRED — the caller owes the operator a
 *      message naming what to print and where.
 *
 * Deferral only ever arises under a fallback mode. In `normal` the answer is
 * exactly what {@link usesBrowserPrint} has always given, whole-job, so turning
 * the switch off restores today's behaviour by construction rather than by
 * matching branches.
 *
 * @param kind - What the job asked to print.
 * @param ctx - The surface, the facility, and the outage switch.
 * @returns Where each document of the job goes.
 */
export function resolvePrintRoute(kind: PrintKind, ctx: PrintRouteContext): PrintRoute {
  const docs = printDocsFor(kind);
  const backendDocs = docs.filter((doc) => backendCanPrint(doc, ctx));
  const rest = docs.filter((doc) => !backendDocs.includes(doc));

  // A phone has no printer to browser-print to. Deliberately not applied in
  // `normal` mode: a mobile surface that browser-prints there is the standing
  // config's own choice (or a facility with no Zebra, where the flow disables
  // printing upstream), and this switch is not the place to revisit it.
  const canBrowserPrint = ctx.fallbackMode === 'normal' || !ctx.isMobile;

  return {
    backendKind: kindForDocs(backendDocs),
    browserDocs: canBrowserPrint ? rest : [],
    deferredDocs: canBrowserPrint ? [] : rest,
  };
}

/**
 * Whether one document can still go to the backend print API.
 *
 * Independent reasons it cannot, checked in the order they override each
 * other: the whole API is reported down, this document's own hardware is
 * reported down, or the facility has no printer for this document in the first
 * place.
 */
function backendCanPrint(doc: PrintDoc, ctx: PrintRouteContext): boolean {
  if (ctx.fallbackMode === 'printApiDown') { return false; }
  if (ctx.fallbackMode === 'zebraDown' && doc === 'label') { return false; }
  if (ctx.fallbackMode === 'copierDown' && doc === 'sheet') { return false; }
  if (ctx.configuredMode === 'browser') { return false; }
  return doc === 'label' ? ctx.capability.hasZebraPrinter : ctx.capability.hasCopierPrinter;
}

/**
 * True when the facility has a Zebra printer the backend print API can target.
 * An unknown location (null/undefined) counts as "no printer" — printing to the
 * browser is always safe, whereas guessing a backend printer is not.
 */
export function locationHasBackendPrinter(location: Location | null | undefined): boolean {
  return !!location?.zebraPrinterName?.trim();
}

/** True when the facility has a copier the backend print API can target. */
export function locationHasCopierPrinter(location: Location | null | undefined): boolean {
  return !!location?.copierPrinterName?.trim();
}

/** Both per-document answers for one facility, as {@link resolvePrintRoute} wants them. */
export function locationCapability(location: Location | null | undefined): PrintCapability {
  return {
    hasZebraPrinter: locationHasBackendPrinter(location),
    hasCopierPrinter: locationHasCopierPrinter(location),
  };
}

/**
 * Whether BROWSER-rendered copies of DOC carry per-item QR codes at this
 * facility.
 *
 * The print API path does not ask this — anything it prints carries QR codes.
 * This is the browser path's either-or, because a browser document is printed
 * on a desk Dymo or through a print dialog at a facility that may have no
 * scanning workflow at all, and a code nobody scans is a code that only costs
 * space.
 *
 * PER DOCUMENT, because the two documents scan differently. The evidence this
 * switch was built to collect came back split: a QR on the Dymo label stock in
 * use does not scan reliably, while the same code on the 8x11 copier sheet is
 * fine. One switch could only answer that by throwing away the half that works.
 * The label answer and the sheet answer are now independent, so a facility can
 * drop QR from its Dymo labels and keep it on its copier sheets — or, once
 * somebody decides that a partly-scannable set of documents is worse than none,
 * turn both off. That decision is a manager's, and this is the field it lands in.
 *
 * Defaults to true for both. The overrides are kill switches, not derivations:
 * until there is real-world evidence for a facility, it prints QR so that
 * evidence can accumulate. Deriving the default from the Zebra would have the
 * five facilities that most need to answer the question print no QR at all to
 * evaluate.
 *
 * `browserQrCodeOverride`, the single switch these two replaced, is still read
 * when neither per-document key is set. A config that predates the split (a
 * deployed copy of locations-config.json, say) therefore keeps whatever it was
 * told, rather than silently reverting a facility to QR-on. Both new keys are
 * written out as `null` on every location rather than omitted — see
 * {@link Location.browserQrCodeOverrideLabel} — and `??` treats that null
 * exactly like an absent key, which is what makes the visible-but-inert default
 * possible.
 */
export function browserQrEnabled(
  location: Location | null | undefined,
  doc: PrintDoc,
): boolean {
  const perDoc = doc === 'label'
    ? location?.browserQrCodeOverrideLabel
    : location?.browserQrCodeOverrideSheet;
  return perDoc ?? location?.browserQrCodeOverride ?? true;
}

/**
 * Whether ANY of DOCS carries QR codes on the browser path here — for the copy
 * that is about a whole job rather than one document.
 *
 * "Set Scale to Actual size or the QR won't scan" is worth saying when even one
 * of the documents about to render carries a code. Callers pass the documents
 * actually rendering in the browser (`PrintRoute.browserDocs`), not everything
 * the job asked for: a Zebra-printed label's QR is the print API's business and
 * no scale setting in a browser dialog can hurt it.
 */
export function anyBrowserQrEnabled(
  location: Location | null | undefined,
  docs: PrintDoc[],
): boolean {
  return docs.some((doc) => browserQrEnabled(location, doc));
}

/**
 * True when the receiving flow can print at all on this surface.
 *
 * Two ways to be able to print, and a facility only needs one:
 *
 *   - It has a printer the backend can target — EITHER printer. A facility with
 *     a copier and no Zebra can still print its copier sheet from a phone, and
 *     used to be told it could print nothing.
 *   - We are on desktop, where the browser prints to the Dymo printer already
 *     attached to that machine.
 *
 * That leaves exactly one combination that cannot print: mobile at a facility
 * with no backend printer at all. There the flow disables the Labels control
 * outright and points at the log table's reprint instead — browser-printing from
 * the phone in someone's hand reaches no printer, so an enable-able toggle would
 * only let the user arm a print that silently did nothing.
 *
 * The desktop half is why this is not simply {@link locationCapability}: a
 * desktop user at Kennewick was being told to go print from the log table while
 * sitting at the machine that reprint would have used. Which path the print then
 * takes is {@link usesBrowserPrint}'s call.
 *
 * @param isMobile   running on a mobile device (iOS/Android).
 * @param capability which printers this facility has (see {@link locationCapability}).
 */
export function printingAvailable(isMobile: boolean, capability: PrintCapability): boolean {
  return capability.hasZebraPrinter || capability.hasCopierPrinter || !isMobile;
}

/**
 * True when printing should go through the browser rather than the backend print
 * API.
 *
 * Deliberately takes no `isMobile`: the printer belongs to the facility, so the
 * device the operator is holding does not change which path can reach it. A
 * Zebra location prints through the API from a desktop just as it does from a
 * phone.
 *
 * This is the WHOLE-JOB answer, and it stays whole-job: it is true only when
 * nothing at all reaches the backend. `zebraDown` therefore does not make it
 * true — the copier sheet still goes to the print API there — which is exactly
 * why the per-document {@link resolvePrintRoute} exists. Call this one for the
 * things that are whole-job facts (does this screen need the named-printer
 * selectors, can it offer per-item QR reprint); call the route for the print.
 *
 * Derived from {@link backendCanPrint} for both documents rather than hand-rolled:
 * its contract is "nothing at all reaches the backend", which is exactly
 * `!backendCanPrint('label', ...) && !backendCanPrint('sheet', ...)`. Hand-rolling
 * it separately is how it drifted from that contract before — a Zebra-only
 * facility under `zebraDown` has nothing left that can reach the backend either,
 * but the old branches never asked that question the same way `resolvePrintRoute`
 * does. Takes `isMobile: false` when building that context: the printer belongs
 * to the facility, not the device, so which surface is asking does not change
 * the answer.
 *
 * @param configuredMode `printMode.mobile` from the receiving-log form config;
 *                       'browser' forces the browser path even where a backend
 *                       printer exists.
 * @param capability     which printers this facility has (see {@link locationCapability}).
 * @param fallbackMode   the device-local outage switch; defaults to `normal`,
 *                       so callers that predate it keep their answer.
 */
export function usesBrowserPrint(
  configuredMode: PrintMode,
  capability: PrintCapability,
  fallbackMode: PrintFallbackMode = 'normal',
): boolean {
  const ctx: PrintRouteContext = { isMobile: false, configuredMode, capability, fallbackMode };
  return !backendCanPrint('label', ctx) && !backendCanPrint('sheet', ctx);
}
